/**
 * Publisher Analytics helpers.
 *
 * The Analytics page is a separate read model from the persisted Publisher dashboard
 * snapshot: range selection, tab state and unsupported-field notes all belong to the
 * Analytics surface itself rather than to releases/payments/events/platforms.
 */
import type {
  PublisherAnalyticsAvailability,
  PublisherAnalyticsInsight,
  PublisherAnalyticsMetricBreakdownItem,
  PublisherAnalyticsMetricCard,
  PublisherAnalyticsPayload,
  PublisherAnalyticsRange,
  PublisherAnalyticsRangePreset,
  PublisherAnalyticsTab,
} from '../types/domain';

export const PUBLISHER_ANALYTICS_RANGE_ITEMS: ReadonlyArray<{
  key: PublisherAnalyticsRangePreset;
  label: string;
}> = [
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
  { key: '90d', label: '90d' },
  { key: 'all', label: 'All Time' },
  { key: 'custom', label: 'Custom' },
];

/** Synthetic release id used only by the Analytics surface for a catalogue aggregate. */
export const PUBLISHER_ANALYTICS_ALL_RELEASES_ID = '__publisher-analytics-all-releases__';

export function isPublisherAnalyticsAllReleasesId(releaseId: string): boolean {
  return releaseId === PUBLISHER_ANALYTICS_ALL_RELEASES_ID;
}

export const PUBLISHER_ANALYTICS_TAB_ITEMS: ReadonlyArray<{
  key: PublisherAnalyticsTab;
  label: string;
}> = [
  { key: 'streams', label: 'Streams' },
  { key: 'platforms', label: 'Platforms' },
  { key: 'geography', label: 'Geography' },
  { key: 'playlists', label: 'Playlists' },
];

function rangePresetLabel(preset: PublisherAnalyticsRangePreset): string {
  switch (preset) {
    case '7d':
      return 'Last 7 days';
    case '30d':
      return 'Last 30 days';
    case '90d':
      return 'Last 90 days';
    case 'all':
      return 'All time';
    case 'custom':
      return 'Custom range';
  }
}

export function publisherAnalyticsRangeLabel(input: {
  preset: PublisherAnalyticsRangePreset;
  startDate?: string | null;
  endDate?: string | null;
}): string {
  if (input.preset === 'custom' && input.startDate && input.endDate) {
    return `${input.startDate} → ${input.endDate}`;
  }
  return rangePresetLabel(input.preset);
}

export function publisherAnalyticsCustomRangeReady(startDate: string, endDate: string): boolean {
  return startDate.trim().length > 0 && endDate.trim().length > 0;
}

function cloneMetricBreakdownItem(item: PublisherAnalyticsMetricBreakdownItem): PublisherAnalyticsMetricBreakdownItem {
  return { ...item };
}

function cloneMetricCard(card: PublisherAnalyticsMetricCard): PublisherAnalyticsMetricCard {
  return {
    ...card,
    breakdown: card.breakdown?.map(cloneMetricBreakdownItem),
  };
}

function cloneInsight(insight: PublisherAnalyticsInsight): PublisherAnalyticsInsight {
  return { ...insight };
}

export function clonePublisherAnalyticsPayload(payload: PublisherAnalyticsPayload): PublisherAnalyticsPayload {
  return {
    ...payload,
    selectedRange: { ...payload.selectedRange },
    keyMetrics: payload.keyMetrics.map(cloneMetricCard),
    streams: {
      ...payload.streams,
      series: payload.streams.series.map((point) => ({ ...point })),
    },
    platforms: {
      rows: payload.platforms.rows.map((row) => ({ ...row })),
      notes: payload.platforms.notes.map(cloneInsight),
    },
    geography: {
      countries: payload.geography.countries.map((row) => ({ ...row })),
      notes: payload.geography.notes.map(cloneInsight),
    },
    playlists: {
      summary: payload.playlists.summary.map(cloneMetricCard),
      rows: payload.playlists.rows.map((row) => ({ ...row })),
      insights: payload.playlists.insights.map(cloneInsight),
    },
    unsupported: payload.unsupported.map(cloneInsight),
  };
}

export function withPublisherAnalyticsRange(
  payload: PublisherAnalyticsPayload,
  input: {
    preset: PublisherAnalyticsRangePreset;
    startDate?: string | null;
    endDate?: string | null;
  }
): PublisherAnalyticsPayload {
  return {
    ...clonePublisherAnalyticsPayload(payload),
    selectedRange: {
      preset: input.preset,
      label: publisherAnalyticsRangeLabel(input),
      startDate: input.preset === 'custom' ? (input.startDate ?? null) : payload.selectedRange.startDate,
      endDate: input.preset === 'custom' ? (input.endDate ?? null) : payload.selectedRange.endDate,
      note: payload.selectedRange.note ?? null,
    },
  };
}

function unavailableMetric(input: {
  key: string;
  label: string;
  note: string;
  documentedByLabelGrid: boolean;
}): PublisherAnalyticsMetricCard {
  return {
    key: input.key,
    label: input.label,
    value: 'Unavailable',
    availability: 'unavailable',
    note: input.note,
    documentedByLabelGrid: input.documentedByLabelGrid,
    providerBacked: false,
  };
}

function unavailableInsight(input: {
  key: string;
  title: string;
  body: string;
  documentedByLabelGrid: boolean;
  availability?: PublisherAnalyticsAvailability;
  note?: string | null;
}): PublisherAnalyticsInsight {
  return {
    key: input.key,
    title: input.title,
    body: input.body,
    availability: input.availability ?? 'unavailable',
    note: input.note ?? null,
    documentedByLabelGrid: input.documentedByLabelGrid,
    providerBacked: false,
  };
}

export function buildUnavailablePublisherAnalytics(input: {
  releaseId: string;
  preset: PublisherAnalyticsRangePreset;
  startDate?: string | null;
  endDate?: string | null;
  note: string;
  reportedAt?: string | null;
}): PublisherAnalyticsPayload {
  const range: PublisherAnalyticsRange = {
    preset: input.preset,
    label: publisherAnalyticsRangeLabel(input),
    startDate: input.preset === 'custom' ? (input.startDate ?? null) : null,
    endDate: input.preset === 'custom' ? (input.endDate ?? null) : null,
    note: input.note,
  };

  return {
    releaseId: input.releaseId,
    source: 'unavailable',
    availability: 'unavailable',
    reportedAt: input.reportedAt ?? null,
    selectedRange: range,
    actionNote: 'Export CSV and Download Report stay unavailable until this build has a real analytics export backend.',
    keyMetrics: [
      unavailableMetric({
        key: 'total-streams',
        label: 'Total Streams',
        note: input.note,
        documentedByLabelGrid: true,
      }),
      unavailableMetric({
        key: 'unique-listeners',
        label: 'Unique Listeners',
        note: input.note,
        documentedByLabelGrid: true,
      }),
      unavailableMetric({
        key: 'playlist-adds',
        label: 'Playlist Adds',
        note: input.note,
        documentedByLabelGrid: true,
      }),
      unavailableMetric({
        key: 'avg-streams-per-listener',
        label: 'Avg Streams / Listener',
        note: input.note,
        documentedByLabelGrid: false,
      }),
    ],
    streams: {
      series: [],
      note: input.note,
      comparisonNote: 'No listeners-over-time comparison is available for this release yet.',
    },
    platforms: {
      rows: [],
      notes: [
        unavailableInsight({
          key: 'platform-breakdown-unavailable',
          title: 'Platform breakdown unavailable',
          body: input.note,
          documentedByLabelGrid: true,
        }),
      ],
    },
    geography: {
      countries: [],
      notes: [
        unavailableInsight({
          key: 'country-breakdown-unavailable',
          title: 'Geography unavailable',
          body: input.note,
          documentedByLabelGrid: true,
        }),
        unavailableInsight({
          key: 'city-breakdown-unavailable',
          title: 'City-level detail unavailable',
          body: 'This build does not return city-level publisher analytics.',
          documentedByLabelGrid: false,
        }),
      ],
    },
    playlists: {
      summary: [
        unavailableMetric({
          key: 'tracked-playlists',
          label: 'Tracked Playlists',
          note: input.note,
          documentedByLabelGrid: true,
        }),
        unavailableMetric({
          key: 'best-placement',
          label: 'Best Placement',
          note: input.note,
          documentedByLabelGrid: true,
        }),
      ],
      rows: [],
      insights: [
        unavailableInsight({
          key: 'playlist-summary-unavailable',
          title: 'Playlist analytics unavailable',
          body: input.note,
          documentedByLabelGrid: true,
        }),
      ],
    },
    unsupported: [
      unavailableInsight({
        key: 'saves-unavailable',
        title: 'Saves unavailable',
        body: 'LabelGrid documents saves, but this Lyrixa build does not return them yet.',
        documentedByLabelGrid: true,
      }),
      unavailableInsight({
        key: 'shares-unavailable',
        title: 'Shares unavailable',
        body: 'LabelGrid documents shares, but this Lyrixa build does not return them yet.',
        documentedByLabelGrid: true,
      }),
      unavailableInsight({
        key: 'shazams-unavailable',
        title: 'Shazams unavailable',
        body: 'LabelGrid documents shazams, but this Lyrixa build does not return them yet.',
        documentedByLabelGrid: true,
      }),
    ],
  };
}
